/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Maybe } from '@codegrade/maybe';
import * as itertools from './itertools';
/**
 * Create an object from an array of key-value tuples or any iterable of pairs.
 *
 * @param vals The key-value pairs to build the object from.
 * @returns An object whose keys and values correspond to the provided pairs.
 */
export function fromEntries(vals) {
    return Object.fromEntries(vals);
}
/**
 * The actual implementation of the curried functions described above.
 *
 * @param obj The object to check for.
 * @param key The key that could be in in `obj`.
 * @returns If `key` is a own property of `obj`.
 */
export function hasAttr(obj, key) {
    switch (arguments.length) {
        case 0:
            return hasAttr;
        case 1:
            return (realKey) => Object.prototype.hasOwnProperty.call(obj, realKey);
        default:
            return Object.prototype.hasOwnProperty.call(obj, key);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function attr(key, obj) {
    switch (arguments.length) {
        case 0:
            return attr;
        case 1:
            return function getAttribute(realObj) {
                return realObj[key];
            };
        default:
            return obj[key];
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function unionAttr(typ, obj, key) {
    if (obj instanceof typ) {
        return Maybe.of(obj[key]);
    }
    return Maybe.empty();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function getItem(obj, key) {
    switch (arguments.length) {
        case 0:
            return getItem;
        case 1:
            return (correctKey) => Maybe.fromNullable(obj[correctKey]);
        default:
            return Maybe.fromNullable(obj[key]);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function filterObject(obj, f) {
    return fromEntries(Object.entries(obj).filter(([key, val]) => f(val, key)));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function forEachObject(obj, fun) {
    Object.entries(obj).forEach(([key, val]) => fun(val, key));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function mapObject(obj, fun) {
    return fromEntries(Object.entries(obj).map(([key, val]) => [key, fun(val, key)]));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function filterMap(arr, filterMapper) {
    return Array.from(itertools.filter(filterMapper, arr));
}
/* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
export function mapToObject(arr, mapper, initial = {}) {
    return itertools.reduce(arr, (acc, el, index) => {
        const [key, value] = mapper(el, index);
        acc[key] = value;
        return acc;
    }, initial);
}
/**
 * Takes an iterable objects creates a map from a specified key to each element of the iterable.
 * If the chosen property is not present or the function returns `undefined`, the key for the
 * mapping will be 'undefined'. Duplicate keys will also overwrite existing elements.
 *
 * @param values The iterable object.
 * @param attrOrGetter Either the attribute name of a property present in each element
 * or a function that can compute one.
 * @returns The map from the chosen key to an element of the iterable.
 */
export function makeLookupMapping(values, attrOrGetter) {
    let getter;
    if (typeof attrOrGetter === 'function') {
        getter = attrOrGetter;
    }
    else {
        getter = attr(attrOrGetter);
    }
    return mapToObject(values, (value, index) => [getter(value, index), value]);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function makeCompleteLookupMapping(ops, lookupAttr) {
    return Object.fromEntries(ops.map(op => [op[lookupAttr], op]));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function makeCompleteLookupMapping2(ops, attrKey, attrValue) {
    return Object.fromEntries(ops.map(op => [op[attrKey], op[attrValue]]));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function mapFilterObject(obj, fun) {
    return fromEntries(filterMap(Object.entries(obj), ([key, val]) => fun(val, key).map(result => [key, result])));
}
/**
 * Check that the given argument is an object.
 *
 * @param obj The element to check to be an object.
 * @returns If `obj` is a record.
 */
export const isObject = (obj) => typeof obj === 'object' && obj !== null && !Array.isArray(obj);
/**
 * Remove the given keys from `obj`.
 *
 * @param obj The object to remove the keys for. The object is not modified.
 * @param omitKeys The keys to remove from `obj`.
 * @returns A fresh object with all elements from `omitKeys` removed.
 */
export function omit(obj, ...omitKeys) {
    const ret = { ...obj };
    for (const key of omitKeys) {
        delete ret[key];
    }
    return ret;
}
